export interface FormulaElement {
  id: string;
  type: FormulaElementType;
  value: any;
  displayName: string;
  position: number;
}

export interface PricingFormula {
  id: string;
  name: string;
  provider_coverage_id: number;
  service_id: number;
  elements: FormulaElement[];
  expression: string; // La fórmula compilada para Math.js
  description?: string; // Descripción opcional de la fórmula
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface FormulaConfiguration {
  provider_coverage_id: number;
  service_id: number;
  formulas: PricingFormula[];
}

export enum FormulaElementType {
  // Campos dinámicos
  DYNAMIC_FIELD = 'dynamic_field',

  // Valores fijos
  NUMBER = 'number',

  // Operadores matemáticos
  ADD = 'add',
  SUBTRACT = 'subtract',
  MULTIPLY = 'multiply',
  DIVIDE = 'divide',
  MODULO = 'modulo',
  POWER = 'power',

  // Funciones matemáticas
  MIN = 'min',
  MAX = 'max',
  ROUND = 'round',
  CEIL = 'ceil',
  FLOOR = 'floor',
  ABS = 'abs',
  SQRT = 'sqrt',

  // Funciones condicionales
  IF = 'if',
  AND = 'and',
  OR = 'or',
  NOT = 'not',

  // Comparadores
  EQUALS = 'equals',
  NOT_EQUALS = 'not_equals',
  GREATER_THAN = 'greater_than',
  LESS_THAN = 'less_than',
  GREATER_EQUAL = 'greater_equal',
  LESS_EQUAL = 'less_equal',

  // Agrupadores
  PARENTHESIS_OPEN = 'parenthesis_open',
  PARENTHESIS_CLOSE = 'parenthesis_close',

  // Constantes predefinidas
  BASE_PRICE = 'base_price',
  DISTANCE_KM = 'distance_km',
  TIME_HOURS = 'time_hours',
  PERCENTAGE = 'percentage'
}

export const FORMULA_ELEMENT_DEFINITIONS = {
  [FormulaElementType.ADD]: { symbol: '+', displayName: 'Sumar', category: 'operators', mathJs: '+' },
  [FormulaElementType.SUBTRACT]: { symbol: '-', displayName: 'Restar', category: 'operators', mathJs: '-' },
  [FormulaElementType.MULTIPLY]: { symbol: '×', displayName: 'Multiplicar', category: 'operators', mathJs: '*' },
  [FormulaElementType.DIVIDE]: { symbol: '÷', displayName: 'Dividir', category: 'operators', mathJs: '/' },
  [FormulaElementType.MODULO]: { symbol: '%', displayName: 'Módulo', category: 'operators', mathJs: 'mod' },
  [FormulaElementType.POWER]: { symbol: '^', displayName: 'Potencia', category: 'operators', mathJs: '^' },

  [FormulaElementType.MIN]: { symbol: 'MIN', displayName: 'Mínimo', category: 'functions', mathJs: 'min' },
  [FormulaElementType.MAX]: { symbol: 'MAX', displayName: 'Máximo', category: 'functions', mathJs: 'max' },
  [FormulaElementType.ROUND]: { symbol: 'ROUND', displayName: 'Redondear', category: 'functions', mathJs: 'round' },
  [FormulaElementType.CEIL]: { symbol: 'CEIL', displayName: 'Redondear Arriba', category: 'functions', mathJs: 'ceil' },
  [FormulaElementType.FLOOR]: { symbol: 'FLOOR', displayName: 'Redondear Abajo', category: 'functions', mathJs: 'floor' },
  [FormulaElementType.ABS]: { symbol: 'ABS', displayName: 'Valor Absoluto', category: 'functions', mathJs: 'abs' },
  [FormulaElementType.SQRT]: { symbol: '√', displayName: 'Raíz Cuadrada', category: 'functions', mathJs: 'sqrt' },

  [FormulaElementType.IF]: { symbol: 'IF', displayName: 'Si Entonces', category: 'conditionals', mathJs: 'conditional' },
  [FormulaElementType.AND]: { symbol: 'AND', displayName: 'Y', category: 'conditionals', mathJs: 'and' },
  [FormulaElementType.OR]: { symbol: 'OR', displayName: 'O', category: 'conditionals', mathJs: 'or' },
  [FormulaElementType.NOT]: { symbol: 'NOT', displayName: 'No', category: 'conditionals', mathJs: 'not' },

  [FormulaElementType.EQUALS]: { symbol: '=', displayName: 'Igual a', category: 'comparators', mathJs: '==' },
  [FormulaElementType.NOT_EQUALS]: { symbol: '≠', displayName: 'Diferente de', category: 'comparators', mathJs: '!=' },
  [FormulaElementType.GREATER_THAN]: { symbol: '>', displayName: 'Mayor que', category: 'comparators', mathJs: '>' },
  [FormulaElementType.LESS_THAN]: { symbol: '<', displayName: 'Menor que', category: 'comparators', mathJs: '<' },
  [FormulaElementType.GREATER_EQUAL]: { symbol: '≥', displayName: 'Mayor o igual', category: 'comparators', mathJs: '>=' },
  [FormulaElementType.LESS_EQUAL]: { symbol: '≤', displayName: 'Menor o igual', category: 'comparators', mathJs: '<=' },

  [FormulaElementType.PARENTHESIS_OPEN]: { symbol: '(', displayName: 'Paréntesis Abrir', category: 'grouping', mathJs: '(' },
  [FormulaElementType.PARENTHESIS_CLOSE]: { symbol: ')', displayName: 'Paréntesis Cerrar', category: 'grouping', mathJs: ')' },

  [FormulaElementType.BASE_PRICE]: { symbol: 'BASE', displayName: 'Precio Base', category: 'constants', mathJs: 'base_price' },
  [FormulaElementType.DISTANCE_KM]: { symbol: 'KM', displayName: 'Distancia (KM)', category: 'constants', mathJs: 'distance_km' },
  [FormulaElementType.TIME_HOURS]: { symbol: 'HRS', displayName: 'Tiempo (Horas)', category: 'constants', mathJs: 'time_hours' },
  [FormulaElementType.PERCENTAGE]: { symbol: '%', displayName: 'Porcentaje', category: 'constants', mathJs: 'percentage' }
};

export interface FormulaValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface FormulaEvaluationContext {
  dynamicFields: { [fieldName: string]: any };
  constants: { [constantName: string]: number };
  basePrice?: number;
}

export interface FormulaEvaluationResult {
  success: boolean;
  result?: number;
  error?: string;
  executionTime?: number;
}